/**
 * Next-generation MUI theme, built from design/tokens.js.
 *
 * Co-exists with the legacy client/src/theme.js — pick one or the other when
 * wrapping a component tree. Components migrated to the new design system
 * should consume this theme (or the raw tokens) directly.
 *
 *   import { themeNext } from './design/theme.next';
 *   <ThemeProvider theme={themeNext}>...</ThemeProvider>
 */

const { createTheme } = require('@mui/material/styles');
const tokens = require('./tokens');

const {
  schemes,
  colorRoles,
  fontFamily,
  fontSize,
  lineHeight,
  fontWeight,
  letterSpacing,
  space,
  radius,
  elevation,
  duration,
  easing,
  breakpoints,
  zIndex,
  layout,
} = tokens;

// MUI needs real colours to work out contrast and hover shades, so the
// palette is built from the raw scheme values, not the CSS variables.
const rawPalette = (scheme) => {
  const group = (prefix) =>
    Object.fromEntries(
      Object.entries(scheme)
        .filter(([k]) => k.startsWith(`${prefix}-`) && /^\d+$/.test(k.slice(prefix.length + 1)))
        .map(([k, v]) => [k.slice(prefix.length + 1), v]),
    );
  return {
    brand: group('brand'),
    brandSolid: group('brand-solid'),
    neutral: group('neutral'),
    success: group('success'),
    warning: group('warning'),
    danger: group('danger'),
    info: group('info'),
  };
};

const buildTheme = (mode = 'light') => {
  const scheme = schemes[mode];
  const P = rawPalette(scheme);
  const roles = Object.fromEntries(Object.keys(colorRoles.dark).map((k) => [k, scheme[`role-${k}`]]));

  return createTheme({
    palette: {
      mode,
      primary: {
        light: P.brand[300],
        main:  P.brand[500],
        dark:  P.brand[700],
        contrastText: mode === 'dark' ? P.neutral[25] : roles.textOnBrand,
      },
      secondary: {
        light: P.neutral[200],
        main:  P.neutral[700],
        dark:  P.neutral[900],
        contrastText: P.neutral[0],
      },
      success: { main: P.success[500], light: P.success[100], dark: P.success[700] },
      warning: { main: P.warning[500], light: P.warning[100], dark: P.warning[700] },
      error:   { main: P.danger[500],  light: P.danger[100],  dark: P.danger[700] },
      info:    { main: P.info[500],    light: P.info[100],    dark: P.info[700] },
      background: {
        default: roles.bg,
        paper:   roles.surface,
      },
      text: {
        primary:   roles.textPrimary,
        secondary: roles.textSecondary,
        disabled:  roles.textTertiary,
      },
      divider: roles.border,
    },

    typography: {
      fontFamily: fontFamily.sans,
      h1: {
        fontFamily: fontFamily.display,
        fontSize:   fontSize['6xl'],
        lineHeight: lineHeight.tight,
        fontWeight: fontWeight.extrabold,
        letterSpacing: letterSpacing.display,
      },
      h2: {
        fontFamily: fontFamily.display,
        fontSize:   fontSize['5xl'],
        lineHeight: lineHeight.tight,
        fontWeight: fontWeight.bold,
        letterSpacing: letterSpacing.tight,
      },
      h3: {
        fontSize:   fontSize['4xl'],
        lineHeight: lineHeight.snug,
        fontWeight: fontWeight.semibold,
        letterSpacing: letterSpacing.tight,
      },
      h4: {
        fontSize:   fontSize['3xl'],
        lineHeight: lineHeight.snug,
        fontWeight: fontWeight.semibold,
      },
      h5: {
        fontSize:   fontSize['2xl'],
        lineHeight: lineHeight.snug,
        fontWeight: fontWeight.semibold,
      },
      h6: {
        fontSize:   fontSize.xl,
        lineHeight: lineHeight.snug,
        fontWeight: fontWeight.semibold,
      },
      subtitle1: {
        fontSize:   fontSize.lg,
        lineHeight: lineHeight.normal,
        fontWeight: fontWeight.medium,
      },
      subtitle2: {
        fontSize:   fontSize.base,
        lineHeight: lineHeight.normal,
        fontWeight: fontWeight.medium,
      },
      body1: {
        fontSize:   fontSize.base,
        lineHeight: lineHeight.normal,
      },
      body2: {
        fontSize:   fontSize.sm,
        lineHeight: lineHeight.normal,
      },
      caption: {
        fontSize:   fontSize.xs,
        lineHeight: lineHeight.normal,
        color:      roles.textSecondary,
      },
      button: {
        fontSize:   fontSize.base,
        lineHeight: 1,
        fontWeight: fontWeight.semibold,
        letterSpacing: letterSpacing.normal,
        textTransform: 'none',
      },
    },

    shape: {
      borderRadius: radius.md,
    },

    spacing: (factor) => `${space[1] * factor}px`, // factor of 4pt grid

    breakpoints: { values: breakpoints },

    zIndex,

    transitions: {
      duration: {
        shortest: 100,
        shorter:  140,
        short:    180,
        standard: 220,
        complex:  320,
        enteringScreen: 280,
        leavingScreen:  220,
      },
      easing: {
        easeInOut: easing.standard,
        easeOut:   easing.standard,
        easeIn:    easing.exit,
        sharp:     easing.exit,
      },
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          'html, body': {
            backgroundColor: roles.bg,
            color: roles.textPrimary,
            fontFamily: fontFamily.sans,
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
          '*, *::before, *::after': { boxSizing: 'border-box' },
          '::selection': { background: P.brand[200], color: P.neutral[900] },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true, disableRipple: false },
        styleOverrides: {
          root: {
            borderRadius: radius.pill,
            padding: `${space[3]}px ${space[6]}px`,
            fontFamily: fontFamily.sans,
            fontWeight: fontWeight.semibold,
            letterSpacing: '-0.005em',
            textTransform: 'none',
            transition: `background-color ${duration.fast} ${easing.standard}, transform ${duration.fast} ${easing.standard}, box-shadow ${duration.fast} ${easing.standard}, color ${duration.fast} ${easing.standard}`,
            '&:focus-visible': {
              outline: 'none',
              boxShadow: `0 0 0 3px ${roles.focusRing}`,
            },
          },
          sizeLarge: {
            padding: `${space[4]}px ${space[8]}px`,
            fontSize: fontSize.base,
            minHeight: 52,
          },
          sizeMedium: {
            padding: `${space[3]}px ${space[6]}px`,
            fontSize: fontSize.sm,
            minHeight: 44,
          },
          sizeSmall: {
            padding: `${space[2]}px ${space[4]}px`,
            fontSize: fontSize.sm,
            minHeight: 36,
          },
          contained: {
            background: P.brandSolid[600],
            color: roles.textOnBrand,
            boxShadow: `inset 0 1px 0 rgba(255,255,255,0.18), 0 6px 16px rgba(91, 56, 168, 0.18), 0 1px 2px rgba(15, 12, 26, 0.06)`,
            '&:hover': {
              background: P.brandSolid[700],
              transform: 'translateY(-1px)',
              boxShadow: `inset 0 1px 0 rgba(255,255,255,0.22), 0 10px 22px rgba(91, 56, 168, 0.25), 0 1px 2px rgba(15, 12, 26, 0.06)`,
            },
            '&:active': {
              transform: 'translateY(0)',
              boxShadow: `inset 0 1px 0 rgba(255,255,255,0.12), 0 3px 8px rgba(91, 56, 168, 0.16)`,
            },
            '&.Mui-disabled': {
              background: P.neutral[200],
              color: P.neutral[500],
              boxShadow: 'none',
            },
          },
          outlined: {
            borderColor: roles.borderStrong,
            borderWidth: '1px',
            color: roles.textPrimary,
            background: roles.surface,
            boxShadow: `0 1px 2px rgba(15, 12, 26, 0.04)`,
            '&:hover': {
              borderColor: roles.brand,
              background: roles.brandSubtle,
              borderWidth: '1px',
            },
          },
          text: {
            color: roles.textPrimary,
            padding: `${space[2]}px ${space[3]}px`,
            '&:hover': { background: roles.surfaceMuted },
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: radius.lg,
            backgroundColor: roles.surface,
            border: `1px solid ${roles.border}`,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            background: roles.surface,
          },
        },
      },
      MuiLink: {
        defaultProps: { underline: 'hover' },
        styleOverrides: {
          root: { color: roles.brand, fontWeight: fontWeight.medium },
        },
      },
    },

    // Expose raw tokens on the theme so styled components can reach them without
    // importing tokens directly: theme.tokens.space[4] etc.
    tokens,
    layout,
  });
};

const themeNext = buildTheme('light');
const themeNextDark = buildTheme('dark');

module.exports = { themeNext, themeNextDark, buildTheme, tokens };
